import { ConfigurationType, SelectedSystemType } from 'data/types';

export type PartitionConfigurationDeleteIdsResult = {
  persistIds: string[];
  localOnlyIds: string[];
};

export type DeleteConfigurationConfirmTextParams = {
  configuration: ConfigurationType;
  selectedSystems: SelectedSystemType[];
  isLastConfiguration: boolean;
};

export const LAST_CONFIGURATION_REPLACEMENT_NOTICE =
  'A new empty configuration will be created in its place.';

export function partitionConfigurationDeleteIds(
  uniqueIds: Array<string | undefined | null>,
  unsavedLocallyCreatedIds: string[]
): PartitionConfigurationDeleteIdsResult {
  const unsavedIds = new Set(unsavedLocallyCreatedIds);
  const persistIds: string[] = [];
  const localOnlyIds: string[] = [];

  uniqueIds.forEach((uniqueId) => {
    if (!uniqueId) {
      return;
    }
    if (unsavedIds.has(uniqueId)) {
      localOnlyIds.push(uniqueId);
      return;
    }
    persistIds.push(uniqueId);
  });

  return { persistIds, localOnlyIds };
}

export function isMissingConfigurationDeleteStatus(
  status: number | undefined
): boolean {
  return status === 404;
}

export function configurationHasSelectedProducts(
  configuration: ConfigurationType,
  selectedSystems: SelectedSystemType[]
): boolean {
  return (
    selectedSystems.some(
      (system) => system.configuration.uniqueId === configuration.uniqueId
    ) ||
    configuration.condensers.some(
      (condenser) =>
        Boolean(condenser.selectedProduct) ||
        condenser.zones.some((zone) => Boolean(zone.selectedProduct))
    )
  );
}

export function configurationHasModifications(
  configuration: ConfigurationType
): boolean {
  return (
    configuration.modified === true ||
    configuration.condensers.some(
      (condenser) =>
        condenser.modified === true ||
        condenser.zones.some((zone) => zone.modified === true)
    )
  );
}

export function deleteConfigurationConfirmText({
  configuration,
  selectedSystems,
  isLastConfiguration
}: DeleteConfigurationConfirmTextParams): string | undefined {
  const hasSelectedProducts = configurationHasSelectedProducts(
    configuration,
    selectedSystems
  );
  const requiresConfirmation =
    isLastConfiguration ||
    hasSelectedProducts ||
    configurationHasModifications(configuration);
  if (!requiresConfirmation) return undefined;

  const question = hasSelectedProducts
    ? 'Are you sure you want to delete this configuration? This will reset all selected products and systems for this configuration!'
    : `Are you sure you want to delete ${configuration.name}?`;
  return isLastConfiguration
    ? `${question} ${LAST_CONFIGURATION_REPLACEMENT_NOTICE}`
    : question;
}
